<template>
  <canvas 
    ref="canvasRef" 
    class="waveform-canvas"
    @click="handleClick"
    @mousemove="handleHover"
    @mouseleave="hoverProgress = null"
  ></canvas>
</template>

<script setup>
const props = defineProps({
  peaks: {
    type: Array,
    default: () => []
  },
  progress: {
    type: Number,
    default: 0
  },
  height: {
    type: Number,
    default: 48
  },
  barWidth: {
    type: Number,
    default: 2
  },
  gap: {
    type: Number,
    default: 1
  },
  // Color controls
  color: {
    type: String,
    default: '#FFFFFF'
  },
  activeColor: {
    type: String,
    default: '#00f2ff'
  },
  /**
   * Playable region as fractions of the FULL waveform, 0..1.
   *
   * Shop tracks stream the whole file but are only previewable between
   * previewStart and previewStart+previewLength. The waveform deliberately
   * still shows the entire track — the buyer should see what they are buying —
   * so the region has to be marked instead: everything outside it is scrimmed
   * back and the played-progress fill is clipped to it.
   *
   * Defaults span the whole width, which makes the region drawing a no-op for
   * every caller that has no preview bounds.
   */
  regionStart: {
    type: Number,
    default: 0
  },
  regionEnd: {
    type: Number,
    default: 1
  },
  /** Tint of the area outside the playable region. */
  regionScrim: {
    type: String,
    default: 'rgba(0,0,0,0.45)'
  }
})

const emit = defineEmits(['seek'])

const canvasRef = ref(null)
const hoverProgress = ref(null)
const processedPeaks = ref([])

// --- Data Processing ---
// 1. Process Peaks (Normalize, Smooth, Expand) independent of Canvas
// Actually, resampling depends on Canvas Width to get pixel-perfect bars.
// So we re-process on resize.

const draw = () => {
    const canvas = canvasRef.value
    if (!canvas) return

    const width = canvas.width
    const height = canvas.height
    const ctx = canvas.getContext('2d')
    
    // Clear
    ctx.clearRect(0, 0, width, height)
    
    // Use prop peaks or fake
    const sourcePeaks = (props.peaks && props.peaks.length) ? props.peaks : []
    
    // If empty/loading, maybe draw distinct empty state? 
    // For now, if empty, draw nothing or flat line.
    if (sourcePeaks.length === 0) return

    // 1. Calculate Grid
    const totalBarWidth = props.barWidth + props.gap
    const totalBars = Math.floor(width / totalBarWidth)
    
    // 2. Resample (Max-in-Bucket)
    let bucketPeaks = []
    const step = sourcePeaks.length / totalBars
    
    for (let i = 0; i < totalBars; i++) {
        const startIdx = Math.floor(i * step)
        const endIdx = Math.floor((i + 1) * step)
        let max = 0
        for (let j = startIdx; j < endIdx && j < sourcePeaks.length; j++) {
            const val = Math.abs(sourcePeaks[j])
            if (val > max) max = val
        }
        bucketPeaks.push(max)
    }

    // 3. Smooth (Moving Average)
    const smoothedPeaks = []
    for(let i = 0; i < bucketPeaks.length; i++) {
        const prev = bucketPeaks[i-1] || bucketPeaks[i]
        const curr = bucketPeaks[i]
        const next = bucketPeaks[i+1] || bucketPeaks[i]
        // [0.2, 0.6, 0.2]
        smoothedPeaks.push((prev * 0.2) + (curr * 0.6) + (next * 0.2))
    }

    // 4. Normalize
    let maxPeak = 0
    for(const p of smoothedPeaks) if(p > maxPeak) maxPeak = p
    if(maxPeak === 0) maxPeak = 1

    // 5. Build Final Array with Expansion & Floor
    const finalBars = smoothedPeaks.map(p => {
        let norm = p / maxPeak
        // EXPANSION 2.5 (User Preferred)
        norm = Math.pow(norm, 2.5) 
        norm = Math.max(norm, 0.01)
        return norm
    })

    // 6. Draw Helper
    const baseline = height * 0.65 // 65% down

    const drawPass = (isActiveLayer) => {
        for (let i = 0; i < finalBars.length; i++) {
            const norm = finalBars[i]
            const x = i * totalBarWidth

            // Calculate Heights
            const topMax = baseline
            const topH = norm * topMax
            const topY = baseline - topH

            const botMax = height - baseline
            const botH = norm * botMax
            const botY = baseline

            if (isActiveLayer) {
                 // Active
                 ctx.fillStyle = props.activeColor
                 ctx.globalAlpha = 1.0
                 ctx.fillRect(x, topY, props.barWidth, topH)
                 
                 ctx.globalAlpha = 0.6
                 ctx.fillRect(x, botY, props.barWidth, botH)
                 ctx.globalAlpha = 1.0
            } else {
                 // Inactive
                 ctx.fillStyle = props.color
                 ctx.globalAlpha = (props.color === '#FFFFFF' || props.color === 'white') ? 0.5 : 1.0
                 // If using custom color, maybe handle alpha differently. 
                 // Sticking to hardcoded 0.5 for white/base logic for now.
                 if (props.color.includes('rgba')) {
                     // assume color has alpha
                 } else {
                     ctx.globalAlpha = 0.5
                 }
                 
                 ctx.fillRect(x, topY, props.barWidth, topH)
                 
                 ctx.globalAlpha = 0.5 * 0.5 // 0.25 effective
                 ctx.fillRect(x, botY, props.barWidth, botH)
                 ctx.globalAlpha = 1.0
            }
        }
    }

    // 7. Render Layers
    
    // Layer 1: Base (Inactive)
    drawPass(false)

    // Region geometry. Clamped and ordered so a caller passing nonsense
    // (end before start, values outside 0..1) degrades to "no region" rather
    // than producing an inverted clip that hides the whole waveform.
    const rs = Math.max(0, Math.min(1, Number(props.regionStart) || 0))
    const reRaw = Number(props.regionEnd)
    const re = Math.max(rs, Math.min(1, Number.isFinite(reRaw) ? reRaw : 1))
    const hasRegion = rs > 0.0001 || re < 0.9999
    const regionLeft = rs * width
    const regionRight = re * width

    // Layer 2: Progress Clip.
    // Clipped to the region as well as to the playhead, so the fill starts at
    // the region's left edge instead of at x=0 — progress is measured against
    // the FULL track, and without this the bars before the preview would paint
    // as "already played".
    if (props.progress > 0) {
        const clipLeft = hasRegion ? regionLeft : 0
        const clipRight = Math.min(width * props.progress, hasRegion ? regionRight : width)
        if (clipRight > clipLeft) {
            ctx.save()
            ctx.beginPath()
            ctx.rect(clipLeft, 0, clipRight - clipLeft, height)
            ctx.clip()
            drawPass(true)
            ctx.restore()
        }
    }

    // Layer 2b: scrim + edges marking the playable region.
    if (hasRegion) {
        ctx.fillStyle = props.regionScrim
        if (regionLeft > 0) ctx.fillRect(0, 0, regionLeft, height)
        if (regionRight < width) ctx.fillRect(regionRight, 0, width - regionRight, height)

        ctx.strokeStyle = 'rgba(255,255,255,0.35)'
        ctx.lineWidth = 1
        for (const edgeX of [regionLeft, regionRight]) {
            // Half-pixel offset so a 1px line lands on a pixel instead of
            // straddling two and rendering as a 2px blur.
            const x = Math.round(edgeX) + 0.5
            if (x <= 0 || x >= width) continue
            ctx.beginPath()
            ctx.moveTo(x, 0)
            ctx.lineTo(x, height)
            ctx.stroke()
        }
    }

    // Layer 3: Hover Line
    if (hoverProgress.value !== null) {
        const hoverX = hoverProgress.value * width
        ctx.beginPath()
        ctx.moveTo(hoverX, 0)
        ctx.lineTo(hoverX, height)
        ctx.strokeStyle = 'rgba(255,255,255,0.8)'
        ctx.lineWidth = 1
        ctx.stroke()
    }
}

// --- Interaction ---
const handleClick = (e) => {
    const canvas = canvasRef.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = Math.max(0, Math.min(1, x / rect.width))
    emit('seek', percent)
}

const handleHover = (e) => {
    const canvas = canvasRef.value
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    hoverProgress.value = Math.max(0, Math.min(1, x / rect.width))
}

// Watchers
watch(() => [props.peaks, props.progress, props.height, props.barWidth, props.gap, props.regionStart, props.regionEnd, hoverProgress.value], () => {
    requestAnimationFrame(draw)
}, { deep: true })

onMounted(() => {
    const observer = new ResizeObserver(() => {
        if(canvasRef.value) {
            canvasRef.value.width = canvasRef.value.offsetWidth
            canvasRef.value.height = canvasRef.value.offsetHeight
            draw()
        }
    })
    if(canvasRef.value) observer.observe(canvasRef.value)
})

</script>

<style scoped>
.waveform-canvas {
  width: 100%;
  height: 100%;
  display: block;
  cursor: pointer;
  /* No drop-shadow. The bars are 2px with a 1px gap, so a 1px black
     shadow around each one filled every gap and the waveform read as a
     dark solid block instead of bars on the page's own ground — which
     is exactly the "dark background" it looked like it had. The canvas
     itself only ever clearRect()s, so nothing else paints behind it. */
}
</style>
